import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function SaskatoonReplacement() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof replacement cost in Saskatoon?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "For a standard home in Saskatoon, roof replacement typically ranges from $6,000 to $15,000. We provide free, honest quotes that cover all materials, labor, and cleanup with zero hidden fees.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need a permit for roof replacement in Saskatoon?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Standard shingle-over-decking replacement typically requires no permit in Saskatoon, but structural repairs to trusses or decking do require one from the city.",
        },
      },
      {
        "@type": "Question",
        name: "How quickly can you respond to an emergency in Saskatoon?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We prioritize active leaks and storm damage in Saskatoon. In most cases, we can have an estimator out for a roof assessment within 24-48 hours to secure your property.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Saskatoon",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Replacement",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Saskatoon",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Saskatoon",
        item: "https://www.saskroofingcompany.com/areas/saskatoon",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Replacement",
        item: "https://www.saskroofingcompany.com/areas/saskatoon/replacement",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Replacement Saskatoon | Sask Roofing"
        description="Professional roof replacement in Saskatoon, SK. We install durable, weather-resistant roofs designed for the Bridge City. Free inspection and fixed quotes."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
              alt="Saskatoon Roof Replacement"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Replacement in Saskatoon
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Protect your Saskatoon home with a roofing system built for the
                Bridge City's extreme climate. From -40°C winters to summer
                hailstorms, we install premium roofs that stand the test of
                time. Honest, fixed pricing and expert local crews.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Get Your Free Inspection</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  Is It Time to Replace Your Saskatoon Roof?
                </h2>
                <p className="text-lg text-muted-foreground">
                  The climate in Saskatoon puts immense stress on exterior
                  materials. When shingles start to fail, water finds its way
                  in, leading to ruined insulation, mold, and expensive
                  structural damage. Knowing when to replace your roof is
                  crucial for protecting your investment.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Watch for these critical warning signs:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>End of Lifespan:</strong> Your roof is
                        approaching 20-25 years old and showing overall signs of
                        deterioration.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Curling Shingles:</strong> The edges of your
                        shingles are turning upward, meaning they are no longer
                        sealing out water.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Bald Spots:</strong> Significant granule loss,
                        often visible in your gutters or as dark patches on the
                        roof itself.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Frequent Repairs:</strong> You are constantly
                        patching leaks or replacing blown-off shingles after
                        every storm.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/d8293da3-ac72-4a20-844b-7ce399944a1a.png&output=webp&q=60"
                  alt="Inspecting a roof in Saskatoon"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Engineered for the Bridge City
              </h2>
              <p className="text-lg text-muted-foreground">
                We don't use standard builder-grade materials. Our roofing
                systems are specifically selected to handle the unique weather
                patterns of the Saskatoon area.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Hail & Storm Defense</h3>
                <p className="text-muted-foreground">
                  Summer storms in Saskatoon can be brutal. We install
                  high-impact resistant shingles designed to survive hail
                  strikes that would destroy a standard roof.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Winter Moisture Protection
                </h3>
                <p className="text-muted-foreground">
                  To combat ice damming during freeze-thaw cycles, we install
                  premium ice and water barriers along all eaves and critical
                  roof valleys.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Superior Ventilation</h3>
                <p className="text-muted-foreground">
                  A poorly ventilated roof rots from the inside out. We ensure
                  your attic has the proper intake and exhaust balance to extend
                  the life of your new roof.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Saskatoon
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Navigating local building codes can be confusing, but we
                  handle it for you. In Saskatoon, a standard residential roof
                  replacement (where we are simply tearing off old shingles and
                  installing new ones over existing decking) typically does not
                  require a building permit. However, if your roof has suffered
                  structural damage—such as broken trusses from heavy snow loads
                  or rotted decking that needs extensive replacement—a permit
                  from the City of Saskatoon may be required. We assess your
                  roof's structural integrity during our free inspection and
                  manage all necessary permitting and compliance, ensuring your
                  new roof meets all local and provincial building codes.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Saskatoon
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Saskatoon is no stranger to severe summer hailstorms and high
                  wind events that can devastate a roof in minutes. If your roof
                  has been damaged by a storm, dealing with insurance can be
                  stressful. We specialize in storm damage restoration and
                  frequently work with local insurance adjusters. If you suspect
                  damage, call us for a free inspection before filing a claim.
                  We will document the damage with detailed photos and provide a
                  comprehensive report for your adjuster, ensuring you get the
                  full coverage you are entitled to for a proper roof
                  replacement.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Saskatoon Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For Saskatoon's demanding climate, we typically recommend
                  premium architectural shingles from trusted manufacturers like
                  IKO, CertainTeed, or GAF. Specifically, we often suggest Class
                  4 impact-resistant shingles to combat the frequent hailstorms
                  in the region. These shingles are engineered to withstand
                  severe weather and come with robust manufacturer warranties.
                  When combined with our local workmanship warranty, you get a
                  roofing system that provides long-lasting peace of mind
                  against the harsh Saskatchewan elements.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      500+
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Roofs in Saskatoon
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Class 4
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Impact Resistance
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      130km/h
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Wind Rating
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Local Crews
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/d8293da3-ac72-4a20-844b-7ce399944a1a.png&output=webp&q=60"
                  alt="Saskatoon roofing installation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Saskatoon Roof Replacement Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We make getting a new roof simple and straightforward for
                  Saskatoon homeowners, with clear communication every step of
                  the way.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Thorough Assessment
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We inspect your current roof, decking, and attic to
                        determine exactly what needs to be replaced.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        No-Surprise Quote
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You receive a fixed-price written estimate. We don't
                        play games with pricing or hit you with hidden fees
                        later.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Expert Installation
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        Our local crews remove the old roof, install new
                        underlayment, and lay your premium shingles with
                        precision.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Final Inspection & Cleanup
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We clean your property thoroughly, including a magnetic
                        sweep for nails, ensuring your yard is safe and
                        spotless.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Saskatoon roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Saskatoon:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/saskatoon"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Saskatoon roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/saskatoon/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Repair in Saskatoon
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Saskatoon Roof Replacement FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof replacement cost in Saskatoon?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  For a standard home in Saskatoon, roof replacement typically
                  ranges from $6,000 to $15,000. We provide free, honest quotes
                  that cover all materials, labor, and cleanup with zero hidden
                  fees.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do I need a permit for roof replacement in Saskatoon?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Standard shingle-over-decking replacement typically requires
                  no permit in Saskatoon, but structural repairs to trusses or
                  decking do require one from the city.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How quickly can you respond to an emergency in Saskatoon?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  We prioritize active leaks and storm damage in Saskatoon. In
                  most cases, we can have an estimator out for a roof assessment
                  within 24-48 hours to secure your property.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Ready for a Better Roof?
            </h2>
            <p className="text-xl opacity-90">
              Get a free, no-obligation inspection and a fixed-price quote from
              Saskatoon's trusted roofing experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
